import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Toggle } from "@carbon/react";
import { Rocket, Share, Undo, Edit } from "@carbon/icons-react";
import { MaterialResponse } from "../../../types";
import { useUser } from "../../../context";
import { ABILITIES } from "../../../utils";
import "./actions.scss";

type ActionsProps = {
  isOwner: boolean;
  goToBidding: () => void;
  material: MaterialResponse;
  isResponsible: boolean;
  onClick: () => void;
  onClickRegain: () => void;
  onToggleMaterialStatus: (id: string) => void;
};

/**
 * The material detail page's trailing action row — rebuilt on Carbon `Button`/`Toggle` (Task 8).
 * Rendered into `PageHeader`'s own `action` slot by `components/Material/Material.tsx`; the
 * leading "back" affordance that used to live in this same component (`GoBackArrow`) now sits
 * above `PageHeader` in that parent instead — the same split
 * `containers/Sites/EditSite/EditSite.tsx` already established between its own back button and
 * header. Every gating condition (`isOwner`/`isResponsible`/`material.active`/role checks) is
 * unchanged from the pre-Carbon version.
 *
 * The Edit control used to be an icon-only `<Button link>` with an `aria-hidden` icon and no
 * accessible name at all — `e2e/materials.spec.ts`'s own "UN-DRIVABLE SURFACES" note flags this
 * exact gap and names "whichever later task in this programme rebuilds Actions.tsx in Carbon" as
 * its owner. It now carries real visible text (`cta.edit`), which doubles as its accessible name.
 *
 * The active/inactive control is Carbon's own `Toggle`, not the shared Tailwind `Toggle`
 * (`components/UI/Toggle`) the pre-Carbon page used — Carbon's already renders `role="switch"` +
 * `aria-checked` (exactly what the e2e safety net asserts on), and its own `labelA`/`labelB` side
 * label reproduces the "ACTIVE"/"INACTIVE" text the old bordered status box showed, so no separate
 * status `Tag` is needed alongside it.
 */
function Actions({
  isOwner,
  onClick,
  material,
  goToBidding,
  isResponsible,
  onClickRegain,
  onToggleMaterialStatus,
}: ActionsProps) {
  const { t } = useTranslation();
  const { can } = useUser();

  const statusToggle = (
    <Toggle
      id={`material-status-${material?.id}`}
      className="material-actions__status"
      size="sm"
      toggled={material?.active}
      onClick={() => onToggleMaterialStatus(material?.id)}
      labelA={t("cta.inactive")}
      labelB={t("cta.active")}
      aria-label={t("material.toggleStatus")}
    />
  );

  return (
    <div className="material-actions">
      {can(ABILITIES.CREATE_ORDERS) && material?.active && isResponsible && (
        <Button onClick={goToBidding} renderIcon={Rocket}>
          {t("cta.launch")}
        </Button>
      )}
      {isOwner ? (
        <>
          <Button kind="secondary" onClick={onClick} renderIcon={Share}>
            {t("material.delegate")}
          </Button>
          {material?.user?.id !== material?.owner?.id && (
            <Button kind="secondary" onClick={onClickRegain} renderIcon={Undo}>
              {t("material.retrieveMaterial")}
            </Button>
          )}
          <Button
            kind="tertiary"
            as={Link}
            to={`/materials/${material?.id}/edit`}
            renderIcon={Edit}
          >
            {t("cta.edit")}
          </Button>
          {statusToggle}
        </>
      ) : null}
      {isResponsible && !isOwner && (
        <>
          <Button
            kind="tertiary"
            as={Link}
            to={`/materials/${material?.id}/edit`}
            renderIcon={Edit}
          >
            {t("cta.edit")}
          </Button>
          {statusToggle}
        </>
      )}
    </div>
  );
}

export default Actions;
